import { Model } from 'dva'
import { Organisation } from './types'
import { OrganisationsService } from '../../Service'
import { IsObject, isAppWebsite } from '../../Utils/helpers'
import { FailureHandler } from 'Utils/ApiHelper'

const Organisations: Model = {
  namespace: 'organisations',
  state: {
    list: [],
  },
  effects: {
    *list(
      {
        useCache = true,
        filterOptions = {},
        setLoading = () => {},
        onSuccess = () => {},
        onFailure = () => {},
      },
      { call, put, select }
    ): any {
      setLoading(true)
      try {
        let organisations = yield select((state: any) => state.organisations.list)

        let requestOptions: any = {
          ordering: ['name'],
          ...filterOptions,
        }
        // Fetch allowed organisations only, currently applicable only for Studio
        if (!isAppWebsite) {
          const user_permissions = yield select((state: any) => state.auth?.user?.permissions)
          if (!('all_orgs' in user_permissions?.org_dependent_access)) {
            let allowed_organisations = Object.keys(user_permissions?.org_dependent_access)
            if (allowed_organisations.length > 0) {
              requestOptions['uuid'] = allowed_organisations
            }
          }
        }
        if (!(organisations.length > 0 && useCache)) {
          organisations = yield call(OrganisationsService.organisationsList, {
            ...requestOptions,
          })
          yield put({ type: 'modifyList', payload: organisations, modifyType: 'REPLACE_LIST' })
        }
        onSuccess(organisations)
      } catch (err: any) {
        console.error('Error fetching organisations', { err })
        onFailure(err)
      }
      setLoading(false)
    },
    *retrieve(
      { id, uuid, setLoading = () => {}, onSuccess = () => {}, onFailure = () => {} },
      { call, put, select }
    ): any {
      setLoading(true)
      try {
        let organisation = yield select((state: any) =>
          state.organisations.list.find((organisation: Organisation) => organisation.id === id)
        )
        if (!organisation) {
          organisation = yield call(OrganisationsService.organisationsRetrieve, {
            organisationUuid: uuid,
          })
        }
        yield put({ type: 'modifyList', payload: organisation })
        onSuccess(organisation)
      } catch (err) {
        FailureHandler('Could not find the organisation!', err, onFailure)
      }
      setLoading(false)
    },
    *create(
      { data = {}, setLoading = () => {}, onSuccess = () => {}, onFailure = () => {} },
      { call, put }
    ): any {
      setLoading(true)
      try {
        const response = yield call(OrganisationsService.organisationsCreate, {
          requestBody: data,
        })
        yield put({ type: 'modifyList', payload: response })
        onSuccess(response)
      } catch (err) {
        FailureHandler('Error adding an organisation', err, onFailure)
      }
      setLoading(false)
    },
    *partialUpdate(
      { id, uuid, data, setLoading = () => {}, onSuccess = () => {}, onFailure = () => {} },
      { call, put }
    ): any {
      setLoading(true)
      try {
        const response = yield call(OrganisationsService.organisationsPartialUpdate, {
          organisationUuid: uuid,
          requestBody: data,
        })
        yield put({ type: 'modifyList', payload: response })
        onSuccess(response)
      } catch (err) {
        FailureHandler('Error editing a organisation', err, onFailure)
      }
      setLoading(false)
    },
    *destroy(
      { uuid, setLoading = () => {}, onSuccess = () => {}, onFailure = () => {} },
      { call, put, select }
    ): any {
      setLoading(true)
      let organisation = yield select((state: any) =>
        state.organisations.list.find((organisation: Organisation) => organisation?.uuid === uuid)
      )
      try {
        yield call(OrganisationsService.organisationsDestroy, { organisationUuid: uuid })
        yield put({ type: 'modifyList', payload: organisation?.id })
        onSuccess()
      } catch (err) {
        FailureHandler('Error deleting a organisation', err, onFailure)
      }
      setLoading(false)
    },
  },
  reducers: {
    modifyList(state, action) {
      // clone the list
      let list = [...state.list]
      if (action.modifyType === 'REPLACE_LIST') {
        list = action.payload
      } else if (action.modifyType === 'DELETE_ITEM') {
        list = list.filter((l) => {
          return l.uuid != action.payload
        })
      } else if (IsObject(action.payload)) {
        // if the payload is an object, it's a single object, so add it to the list
        list = list.filter((l: Organisation) => l.id !== action.payload.id)
        list.push(action.payload)
      } else if (Array.isArray(action.payload)) {
        // if the payload is an array, it's a list of objects, so add them to the list
        list = list
          .filter(
            (l: Organisation) => !action.payload.map((p: Organisation) => p.id).includes(l.id)
          )
          .concat(action.payload)
      } else if (Number.isInteger(action.payload)) {
        // if the payload is an integer, it's an id sent in delete operations, so remove it from the list
        list = list.filter((l: Organisation) => l.id !== action.payload)
      }
      return {
        ...state,
        list: list,
      }
    },
    resetOrganisations(state, action) {
      return {
        ...state,
        list: [],
      }
    },
  },
}

export default Organisations
